import React from 'react';
import { HighlightWithPlayer } from '../../types/highlights.types';
import { FaPlay, FaDownload, FaShareAlt } from 'react-icons/fa';
import '../../styles/components/highlights.scss';

interface HighlightCardProps {
  highlight: HighlightWithPlayer;
  variant?: 'list' | 'featured';
  onPlay: (highlight: HighlightWithPlayer) => void;
  onDownload: (highlight: HighlightWithPlayer) => void;
  onShare: (highlight: HighlightWithPlayer) => void;
}

const HighlightCard: React.FC<HighlightCardProps> = ({
  highlight,
  variant = 'list',
  onPlay,
  onDownload,
  onShare,
}) => {
  const formatDuration = (seconds: number): string => {
    return `${seconds.toFixed(1)}s`;
  };

  const getTagDisplay = (tag: string): string => {
    // Convert tags like "OneK" to "1K", "TwoK" to "2K", etc.
    const tagMap: { [key: string]: string } = {
      'OneK': '1K',
      'TwoK': '2K',
      'ThreeK': '3K',
      'FourK': '4K',
      'Ace': 'ACE',
    };
    return tagMap[tag] || tag;
  };

  // Generate title from primary tag
  const getPrimaryTag = (): string => {
    // Priority: Ace > 4K > 3K > 2K > 1K > Clutch > Other
    const priorityTags = ['Ace', 'FourK', 'ThreeK', 'TwoK', 'OneK'];
    for (const tag of priorityTags) {
      if (highlight.tags.includes(tag)) {
        return getTagDisplay(tag);
      }
    }
    // Check for clutch tags
    const clutchTag = highlight.tags.find(t => t.includes('1v'));
    if (clutchTag) return clutchTag;

    return `${highlight.killCount}K`;
  };

  const getHighlightTitle = (): string => {
    const primaryTag = getPrimaryTag();
    // You could add map name here if it's available in the highlight data
    return primaryTag;
  };

  // Get player avatar URL (if available from playerAvatar prop)
  const getPlayerAvatar = (): string => {
    if (highlight.playerAvatar) {
      return highlight.playerAvatar;
    }
    // Default avatar placeholder
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(highlight.playerName || 'Player')}&background=4294d0&color=fff&size=120`;
  };

  return (
    <div className={`highlight-card highlight-card--${variant}`} onClick={() => onPlay(highlight)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onPlay(highlight); }}>
      {/* Player Avatar */}
      <div className="highlight-card__avatar">
        <img src={getPlayerAvatar()} alt={highlight.playerName || 'Player'} className="avatar-img" />
      </div>

      {/* Video Thumbnail */}
      <div className="highlight-card__thumbnail">
        <video
          className="highlight-card__video-preview"
          src={highlight.clipUrl}
          preload="metadata"
        />
        <div className="highlight-card__play-overlay">
          <FaPlay className="highlight-card__play-icon" />
        </div>
      </div>

      {/* Info */}
      <div className="highlight-card__info">
        <div className="highlight-card__title">{getHighlightTitle()}</div>
        <div className="highlight-card__meta">
          <span className="highlight-card__round">Round {highlight.roundNumber}</span>
        </div>
      </div>
    </div>
  );
};

export default HighlightCard;
